import {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { useAppTranslation } from 'hooks/use-translates';

const Button = lazy(() => import('core/generic/elements/button'));

export type CodeRequestButtonProps = {
	seconds?: number;
	/** Flipping this restarts the countdown. */
	triggerRenew?: boolean;
	onClick?: () => void;
};

const SECONDS_IN_MINUTE = 60;
const TICK_MS = 1000;

const formatSecondsToTime = (totalSeconds: number) => {
	const minutes = Math.floor(totalSeconds / SECONDS_IN_MINUTE);
	const seconds = totalSeconds % SECONDS_IN_MINUTE;

	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const CodeRequestButton = ({
	seconds = 30,
	triggerRenew,
	onClick,
}: CodeRequestButtonProps) => {
	const [secondsLeft, setSecondsLeft] = useState(seconds);
	const { t } = useAppTranslation();

	// One interval for the whole countdown rather than one per tick, and no
	// separate `canRequest` state to drift out of sync with the counter.
	useEffect(() => {
		if (secondsLeft <= 0) return;

		const timer = setInterval(
			() => setSecondsLeft((previous) => Math.max(previous - 1, 0)),
			TICK_MS
		);

		return () => clearInterval(timer);
	}, [secondsLeft]);

	// Restart on renew only; the previous version also reset on mount, which
	// re-ran the countdown for no reason.
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		setSecondsLeft(seconds);
	}, [triggerRenew, seconds]);

	const canRequest = secondsLeft <= 0;

	const handleClick = useCallback(() => {
		if (canRequest) onClick?.();
	}, [canRequest, onClick]);

	return (
		<Suspense fallback={null}>
			<Button
				type="button"
				label={
					canRequest
						? t('verification.resend_code_button_label')
						: `${t('verification.request_new_button_label')} ${formatSecondsToTime(secondsLeft)}`
				}
				variant="secondary"
				disabled={!canRequest}
				fullWidth={true}
				onClick={handleClick}
				dimension="large"
			/>
		</Suspense>
	);
};

export default CodeRequestButton;
